
import {
	validateName,
	validateEmail,
	validateNotEmpty,
	validateSelectHasValue,
	validateDate,
	validatePositiveNumber,
	validateAtLeastOneCheckbox,
	showError,
	hideError
} from './input';

export class Enquiry {
	firstName: string = '';
	lastName: string = '';
	partnerFirstName: string = '';
	partnerLastName: string = '';
	email: string = '';
	weddingDate: string = '';
	hasWeddingPlanner: boolean = false;
	weddingPlannerDetails: string = '';
	numberOfGuests: number = 0;
	weddingVenue: string = '';
	invitationDate: string = '';
	budgetRange: string = '';
	products: string[] = [];
	productOtherDetails: string = '';
	creativeDirection: string = '';
	pinterestBoard: string = '';
	embellishments: string[] = [];
	additionalComments: string = '';
	hearAboutUs: string = '';
	hearAboutUsOtherDetails: string = '';
}

export class EnquiryPage {

	public setupPage() {
		this.setupOptionalInputFields();
		this.setupFormValidation();
	}

	// We have a number of hidden optional input fields that only appear when the user selects and option that requires them to add more details.
	// For example, if they select "Yes" to the "do you have a wedding planner" then we reveal the input field to enter their wedding planner's details.
	// Likewise, if they select "No" the details field is hidden.
	// This function sets up the logic to show/hide those optional fields based on user selections (visibility is set using the css class called "invisible").
	private setupOptionalInputFields() {
		// Wedding Planner Details
		const weddingPlannerYes = document.getElementById('wedding-planner-yes') as HTMLInputElement;
		const weddingPlannerNo = document.getElementById('wedding-planner-no') as HTMLInputElement;
		const weddingPlannerDetails = document.getElementById('wedding-planner-details') as HTMLInputElement;
		
		if (!weddingPlannerYes) {
			throw new Error('Element with id "wedding-planner-yes" not found');
		}
		if (!weddingPlannerNo) {
			throw new Error('Element with id "wedding-planner-no" not found');
		}
		if (!weddingPlannerDetails) {
			throw new Error('Element with id "wedding-planner-details" not found');
		}
		
		const toggleWeddingPlannerDetails = () => {
			if (weddingPlannerYes.checked) {
				weddingPlannerDetails.classList.remove('invisible');
			} else {
				weddingPlannerDetails.classList.add('invisible');
			}
		};
		
		weddingPlannerYes.addEventListener('change', toggleWeddingPlannerDetails);
		weddingPlannerNo.addEventListener('change', toggleWeddingPlannerDetails);
		
		// Product Other Details
		const productOther = document.getElementById('product-other') as HTMLInputElement;
		const productOtherDetails = document.getElementById('product-other-details') as HTMLInputElement;
		
		if (!productOther) {
			throw new Error('Element with id "product-other" not found');
		}
		if (!productOtherDetails) {
			throw new Error('Element with id "product-other-details" not found');
		}
		
		productOther.addEventListener('change', () => {
			if (productOther.checked) {
				productOtherDetails.classList.remove('invisible');
			} else {
				productOtherDetails.classList.add('invisible');
			}
		});
		
		// Hear About Us Other Details
		const hearAboutUs = document.getElementById('hear-about-us') as HTMLSelectElement;
		const hearAboutUsOtherDetails = document.getElementById('hear-about-us-other-details') as HTMLInputElement;
		
		if (!hearAboutUs) {
			throw new Error('Element with id "hear-about-us" not found');
		}
		if (!hearAboutUsOtherDetails) {
			throw new Error('Element with id "hear-about-us-other-details" not found');
		}
		
		hearAboutUs.addEventListener('change', () => {
			if (hearAboutUs.value === 'Other') {
				hearAboutUsOtherDetails.classList.remove('invisible');
			} else {
				hearAboutUsOtherDetails.classList.add('invisible');
			}
		});
	}

	/**
	 * Sets up form validation on submit button click
	 */
	private setupFormValidation(): void {
		const submitButton = document.getElementById('submit-button') as HTMLButtonElement;
		
		if (!submitButton) {
			throw new Error('Element with id "submit-button" not found');
		}
		
		submitButton.addEventListener('click', (event) => {
			event.preventDefault();
			this.handleSubmit();
		});
	}

	/**
	 * Handles form submission
	 */
	private handleSubmit(): void {
		const submitButton = document.getElementById('submit-button') as HTMLButtonElement;
		const submitMessage = document.getElementById('submit-message') as HTMLParagraphElement;
		
		if (!submitButton || !submitMessage) {
			throw new Error('Submit button or submit message element not found');
		}
		
		const submitBtnTextOriginal = submitButton.textContent;
		
		// Clear any previous messages
		submitMessage.textContent = '';
		submitMessage.classList.remove('error', 'success');
		
		// Disable button to prevent multiple submissions
		submitButton.disabled = true;
		
		const enquiry: Enquiry | null = this.validateForm();
		if(enquiry) {
			// Form is valid, proceed with submission
			console.log('Submitting enquiry...');
			submitButton.textContent = 'SUBMITTING...';
			
			this.sendEnquiry(enquiry).then((response) => {
				if(response.success) {
					submitButton.textContent = 'SUBMITTED!';
					submitMessage.textContent = 'Thank you! Your enquiry has been submitted successfully. Sarah will be in touch soon.';
					console.log('Enquiry submitted successfully');
				} else {
					submitButton.textContent = submitBtnTextOriginal;
					submitMessage.textContent = `Sorry, we couldn't submit your enquiry: ${response.message}`;
					submitMessage.classList.add('error');
					console.error('Enquiry submission failed:', response.message);
				}
			}).catch((error) => {
				submitButton.textContent = submitBtnTextOriginal;
				submitMessage.textContent = 'An error occurred. Please try again later.';
				submitMessage.classList.add('error');
				console.error('Error during enquiry submission:', error);
			}).finally(() => {
				// Re-enable button after 3 seconds (unless successful)
				setTimeout(() => {
					submitButton.disabled = false;
					if(submitButton.textContent !== 'SUBMITTED!') {
						submitButton.textContent = submitBtnTextOriginal;
					}
				}, 3000);
			});
		} else {
			// Validation failed, re-enable button immediately
			submitButton.disabled = false;
		}
	}

	/**
	 * Validates all form fields and shows/hides error messages accordingly
	 * @returns Enquiry object if validation passes, null if validation fails
	 */
	private validateForm(): Enquiry | null {
		let isValid = true;
		const enquiry = new Enquiry();

		// Validate Your Name (first and last name)
		const firstName = document.getElementById('first-name') as HTMLInputElement;
		const lastName = document.getElementById('last-name') as HTMLInputElement;
		const nameError = document.getElementById('name-error') as HTMLElement;
		
		if (!firstName || !lastName || !nameError) {
			throw new Error('Name input elements not found');
		}
		
		const fullName = `${firstName.value} ${lastName.value}`.trim();
		if (!validateName(fullName)) {
			showError(nameError);
			isValid = false;
		} else {
			hideError(nameError);
			enquiry.firstName = firstName.value.trim();
			enquiry.lastName = lastName.value.trim();
		}

		// Validate Partner's Name (first and last name)
		const partnerFirstName = document.getElementById('partner-first-name') as HTMLInputElement;
		const partnerLastName = document.getElementById('partner-last-name') as HTMLInputElement;
		const partnerNameError = document.getElementById('partner-name-error') as HTMLElement;
		
		if (!partnerFirstName || !partnerLastName || !partnerNameError) {
			throw new Error('Partner name input elements not found');
		}
		
		const partnerFullName = `${partnerFirstName.value} ${partnerLastName.value}`.trim();
		if (!validateName(partnerFullName)) {
			showError(partnerNameError);
			isValid = false;
		} else {
			hideError(partnerNameError);
			enquiry.partnerFirstName = partnerFirstName.value.trim();
			enquiry.partnerLastName = partnerLastName.value.trim();
		}

		// Validate Email
		const email = document.getElementById('email') as HTMLInputElement;
		const emailError = document.getElementById('email-error') as HTMLElement;
		
		if (!email || !emailError) {
			throw new Error('Email input elements not found');
		}
		
		if (!validateEmail(email.value)) {
			showError(emailError);
			isValid = false;
		} else {
			hideError(emailError);
			enquiry.email = email.value.trim();
		}

		// Validate Wedding Date
		const weddingDate = document.getElementById('wedding-date') as HTMLInputElement;
		const weddingDateError = document.getElementById('wedding-date-error') as HTMLElement;
		
		if (!weddingDate || !weddingDateError) {
			throw new Error('Wedding date input elements not found');
		}
		
		if (!validateDate(weddingDate.value)) {
			showError(weddingDateError);
			isValid = false;
		} else {
			hideError(weddingDateError);
			enquiry.weddingDate = weddingDate.value;
		}

		// Validate Wedding Planner Details (only if "Yes" is selected)
		const weddingPlannerYes = document.getElementById('wedding-planner-yes') as HTMLInputElement;
		const weddingPlannerDetails = document.getElementById('wedding-planner-details') as HTMLInputElement;
		const weddingPlannerError = document.getElementById('wedding-planner-error') as HTMLElement;
		
		if (!weddingPlannerYes || !weddingPlannerDetails || !weddingPlannerError) {
			throw new Error('Wedding planner input elements not found');
		}
		
		enquiry.hasWeddingPlanner = weddingPlannerYes.checked;
		
		if (weddingPlannerYes.checked && !validateNotEmpty(weddingPlannerDetails.value)) {
			showError(weddingPlannerError);
			isValid = false;
		} else {
			hideError(weddingPlannerError);
			if (weddingPlannerYes.checked) {
				enquiry.weddingPlannerDetails = weddingPlannerDetails.value.trim();
			}
		}

		// Validate Number of Guests
		const numberOfGuests = document.getElementById('number-of-guests') as HTMLInputElement;
		const numberOfGuestsError = document.getElementById('number-of-guests-error') as HTMLElement;
		
		if (!numberOfGuests || !numberOfGuestsError) {
			throw new Error('Number of guests input elements not found');
		}
		
		if (!validatePositiveNumber(numberOfGuests.value)) {
			showError(numberOfGuestsError);
			isValid = false;
		} else {
			hideError(numberOfGuestsError);
			enquiry.numberOfGuests = Number(numberOfGuests.value);
		}

		// Validate Wedding Venue
		const weddingVenue = document.getElementById('wedding-venue') as HTMLInputElement;
		const weddingVenueError = document.getElementById('wedding-venue-error') as HTMLElement;
		
		if (!weddingVenue || !weddingVenueError) {
			throw new Error('Wedding venue input elements not found');
		}
		
		if (!validateNotEmpty(weddingVenue.value)) {
			showError(weddingVenueError);
			isValid = false;
		} else {
			hideError(weddingVenueError);
			enquiry.weddingVenue = weddingVenue.value.trim();
		}

		// Validate Invitation Date
		const invitationDate = document.getElementById('invitation-date') as HTMLSelectElement;
		const invitationDateError = document.getElementById('invitation-date-error') as HTMLElement;
		
		if (!invitationDate || !invitationDateError) {
			throw new Error('Invitation date input elements not found');
		}
		
		if (!validateSelectHasValue(invitationDate.value)) {
			showError(invitationDateError);
			isValid = false;
		} else {
			hideError(invitationDateError);
			enquiry.invitationDate = invitationDate.value;
		}

		// Validate Budget Range
		const budgetRange = document.getElementById('budget-range') as HTMLSelectElement;
		const budgetRangeError = document.getElementById('budget-range-error') as HTMLElement;
		
		if (!budgetRange || !budgetRangeError) {
			throw new Error('Budget range input elements not found');
		}
		
		if (!validateSelectHasValue(budgetRange.value)) {
			showError(budgetRangeError);
			isValid = false;
		} else {
			hideError(budgetRangeError);
			enquiry.budgetRange = budgetRange.value;
		}

		// Validate Product Selection (at least one checkbox)
		const productFullService = document.getElementById('product-full-service') as HTMLInputElement;
		const productBeforeTheDay = document.getElementById('product-before-the-day') as HTMLInputElement;
		const productOther = document.getElementById('product-other') as HTMLInputElement;
		const productError = document.getElementById('product-error') as HTMLElement;
		
		if (!productFullService || !productBeforeTheDay || !productOther || !productError) {
			throw new Error('Product checkbox elements not found');
		}
		
		const productCheckboxes = [productFullService, productBeforeTheDay, productOther];
		if (!validateAtLeastOneCheckbox(productCheckboxes)) {
			showError(productError);
			isValid = false;
		} else {
			hideError(productError);
			if (productFullService.checked) enquiry.products.push(productFullService.value);
			if (productBeforeTheDay.checked) enquiry.products.push(productBeforeTheDay.value);
			if (productOther.checked) enquiry.products.push(productOther.value);
		}

		// Validate Product Other Details (only if "Other" is checked)
		const productOtherDetails = document.getElementById('product-other-details') as HTMLInputElement;
		
		if (!productOtherDetails) {
			throw new Error('Product other details input element not found');
		}
		
		if (productOther.checked && !validateNotEmpty(productOtherDetails.value)) {
			// Use the same error as product selection
			showError(productError);
			isValid = false;
		} else if (productOther.checked) {
			enquiry.productOtherDetails = productOtherDetails.value.trim();
		}

		// Validate Creative Direction
		const creativeDirection = document.getElementById('creative-direction') as HTMLTextAreaElement;
		const creativeDirectionError = document.getElementById('creative-direction-error') as HTMLElement;
		
		if (!creativeDirection || !creativeDirectionError) {
			throw new Error('Creative direction input elements not found');
		}
		
		if (!validateNotEmpty(creativeDirection.value)) {
			showError(creativeDirectionError);
			isValid = false;
		} else {
			hideError(creativeDirectionError);
			enquiry.creativeDirection = creativeDirection.value.trim();
		}

		// Pinterest Board (optional)
		const pinterestBoard = document.getElementById('pinterest-board') as HTMLInputElement;
		if (pinterestBoard) {
			enquiry.pinterestBoard = pinterestBoard.value.trim();
		}

		// Embellishments (optional)
		const embellishmentCheckboxes = document.querySelectorAll('input[name="embellishment"]:checked') as NodeListOf<HTMLInputElement>;
		embellishmentCheckboxes.forEach(checkbox => {
			enquiry.embellishments.push(checkbox.value);
		});

		// Additional Comments (optional)
		const additionalComments = document.getElementById('additional-comments') as HTMLTextAreaElement;
		if (additionalComments) {
			enquiry.additionalComments = additionalComments.value.trim();
		}

		// Validate How Did You Hear About Us
		const hearAboutUs = document.getElementById('hear-about-us') as HTMLSelectElement;
		const hearAboutUsError = document.getElementById('hear-about-us-error') as HTMLElement;
		
		if (!hearAboutUs || !hearAboutUsError) {
			throw new Error('Hear about us input elements not found');
		}
		
		if (!validateSelectHasValue(hearAboutUs.value)) {
			showError(hearAboutUsError);
			isValid = false;
		} else {
			hideError(hearAboutUsError);
			enquiry.hearAboutUs = hearAboutUs.value;
		}

		// Validate Hear About Us Other Details (only if "Other" is selected)
		const hearAboutUsOtherDetails = document.getElementById('hear-about-us-other-details') as HTMLInputElement;
		
		if (!hearAboutUsOtherDetails) {
			throw new Error('Hear about us other details input element not found');
		}
		
		if (hearAboutUs.value === 'Other' && !validateNotEmpty(hearAboutUsOtherDetails.value)) {
			// Use the same error as hear about us selection
			showError(hearAboutUsError);
			isValid = false;
		} else if (hearAboutUs.value === 'Other') {
			enquiry.hearAboutUsOtherDetails = hearAboutUsOtherDetails.value.trim();
		}

		// If all validations pass, proceed with form submission
		if (isValid) {
			console.log('Form validation passed. Ready to submit.');
			console.log('Enquiry data:', enquiry);
			return enquiry;
		} else {
			console.log('Form validation failed. Please correct the errors.');
			
			// Scroll to the first visible error element
			const firstVisibleError = document.querySelector('.error:not(.invisible)') as HTMLElement;
			if (firstVisibleError) {
				firstVisibleError.scrollIntoView({ 
					behavior: 'smooth', 
					block: 'center' 
				});
			}
			return null;
		}
	}

	/**
	 * Sends enquiry data to the backend endpoint
	 * @param enquiry The enquiry object to send
	 * @returns Promise resolving to EnquiryResponse
	 */
	async sendEnquiry(enquiry: Enquiry): Promise<EnquiryResponse> {
		// Determine endpoint based on environment (localhost vs production)
		const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
		const requestUrl = isLocalhost 
			? "https://enquiries-worker-staging.llbermingham.workers.dev/enquiry" 
			: "https://enquiries.cartadisera.com/enquiry";

		try {
			const response = await fetch(requestUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(enquiry)
			});

			if (!response.ok) {
				console.warn(`Enquiry request failed with status ${response.status}: ${response.statusText}`);
				const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
				return new EnquiryResponse(false, errorData.error || `Sorry, we couldn't submit your enquiry, please try again later.`);
			}

			const data = await response.json();
			return new EnquiryResponse(true, data.message || "Enquiry submitted successfully!");
		} catch (error) {
			console.error('Network error during enquiry submission:', error);
			return new EnquiryResponse(false, "Network error. Please check your connection and try again.");
		}
	}

}

/**
 * Response object from enquiry endpoint
 */
class EnquiryResponse {
	success: boolean;
	message: string;

	constructor(success: boolean, message: string) {
		this.success = success;
		this.message = message;
	}
}